import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface AnalyticsData {
  totalTickets: number
  openTickets: number
  overdueTickets: number
  slaBreaches: number
  avgResponseTime?: string
  avgResolutionTime?: string
  monthlyTrends: any[]
  departmentStats: any[]
  priorityDistribution: any[]
}

interface SLAMetrics {
  priorityId: string
  priority: {
    name: string
    displayName?: string
    id?: string
  }
  slaTargetHours: number
  totalTickets: number
  compliantTickets: number
  complianceRate: number
  avgResolutionTime: number
  breaches: number
}

const Analytics: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
  const [slaMetrics, setSlaMetrics] = useState<SLAMetrics[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'sla' | 'reports'>('overview')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)

  useEffect(() => {
    fetchAnalytics()
    fetchSLAMetrics()
  }, [])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/analytics/dashboard')
      setAnalyticsData(response.data.data)
    } catch (error) {
      console.error('Error fetching analytics:', error)
      showError('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const fetchSLAMetrics = async () => {
    try {
      const response = await axios.get('/api/analytics/sla-performance')
      // The API returns { success: true, data: { metrics: [...], ... } }
      // Extract the metrics array
      setSlaMetrics(response.data.data?.metrics || [])
    } catch (error) {
      console.error('Error fetching SLA metrics:', error)
    }
  }

  const exportData = async (type: string) => {
    try {
      const response = await axios.get(`/api/analytics/export/${type}`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      showSuccess(`${type} data exported successfully`)
    } catch (error) {
      console.error('Export error:', error)
      showError('Failed to export data')
    }
  }

  const formatTime = (timeString?: string) => {
    if (!timeString) return 'N/A'
    const date = new Date(timeString)
    return date.toLocaleDateString()
  }

  const calculateSLAPercentage = (metrics: SLAMetrics[]) => {
    if (metrics.length === 0) return 0
    const total = metrics.reduce((sum, m) => sum + m.totalTickets, 0)
    if (total === 0) return 0
    const compliant = metrics.reduce((sum, m) => sum + m.compliantTickets, 0)
    return Math.round((compliant / total) * 100)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Analytics & Reporting
          </h1>
          <p className="text-gray-600">
            Comprehensive insights into platform performance and SLA metrics
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('sla')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sla'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SLA Performance
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Reports & Export
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analyticsData && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Total Tickets</dt>
                        <dd className="text-lg font-medium text-gray-900">{analyticsData.totalTickets}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Open Tickets</dt>
                        <dd className="text-lg font-medium text-gray-900">{analyticsData.openTickets}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Overdue Tickets</dt>
                        <dd className="text-lg font-medium text-gray-900">{analyticsData.overdueTickets}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Avg Response Time</dt>
                        <dd className="text-lg font-medium text-gray-900">{formatTime(analyticsData.avgResponseTime)}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Priority Distribution */}
            {analyticsData.priorityDistribution && analyticsData.priorityDistribution.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Priority Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {analyticsData.priorityDistribution.map((priority: any) => (
                    <div key={priority.priorityId} className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{priority._count}</div>
                      <div className="text-sm text-gray-500">{priority.priority.displayName || priority.priority.name}</div>
                      <div 
                        className="w-4 h-4 mx-auto mt-2 rounded-full"
                        style={{ backgroundColor: priority.priority.color }}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Department Statistics */}
            {analyticsData.departmentStats && analyticsData.departmentStats.length > 0 && (
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Department Performance</h3>
                <div className="space-y-3">
                  {analyticsData.departmentStats.map((dept: any) => (
                    <div key={dept.departmentId} className="flex justify-between items-center">
                      <span className="text-gray-700">{dept.department.name}</span>
                      <span className="font-medium text-gray-900">{dept._count} tickets</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* SLA Performance Tab */}
        {activeTab === 'sla' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">SLA Performance Metrics</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">
                    {calculateSLAPercentage(slaMetrics)}%
                  </div>
                  <div className="text-sm text-gray-500">On-time Performance</div>
                </div>
              </div>
              
              <div className="space-y-4">
                {slaMetrics.length > 0 ? (
                  slaMetrics.map((metric) => (
                    <div key={metric.priorityId} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {metric.priority.displayName || metric.priority.name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {metric.totalTickets} total • {metric.slaTargetHours}h target • {metric.complianceRate}% compliance
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-medium text-gray-900">{metric.complianceRate}%</div>
                          <div className="text-sm text-gray-500">Compliance Rate</div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No Active Tickets</h3>
                    <p className="text-gray-500">There are currently no tickets to analyze for SLA performance.</p>
                    <p className="text-sm text-gray-400 mt-2">SLA metrics will appear here once tickets are created and assigned priorities.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Export Data</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => exportData('tickets')}
                  className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export All Tickets
                </button>
                
                <button
                  onClick={() => exportData('sla-performance')}
                  className="flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Export SLA Performance
                </button>
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Export Options</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Tickets:</strong> Complete ticket data with status, priority, and client information</li>
                  <li>• <strong>SLA Performance:</strong> SLA metrics and performance data for analysis</li>
                  <li>• All exports are in CSV format for easy analysis in Excel or other tools</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Analytics
